import { Box, Flex, Text } from "@chakra-ui/react";
import { FeatureFlag } from "../../api/feature-flags";
import { CustomerMap } from "../../domain/customer";
import { CHANGE_TYPE_ADD, CHANGE_TYPE_REMOVE, StagedChange } from "../../domain/feature-flags";
import { CustomerChip } from "./CustomerChip";

export type FeatureFlagSectionProps = {
  flagName: string;
  flag: FeatureFlag;
  customerMap: CustomerMap;
  searchQuery?: string;
  changes?: StagedChange[];
  onStageRemoval?: (cxId: string) => void;
};

export function FeatureFlagSection({
  flagName,
  flag,
  customerMap,
  searchQuery = "",
  changes = [],
  onStageRemoval,
}: FeatureFlagSectionProps) {
  const values = flag.values ?? [];

  function getCustomerName(cxId: string): string | undefined {
    const customer = customerMap[cxId];
    return customer?.rootCustomer.email ?? undefined;
  }

  function matchesSearch(cxId: string): boolean {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    const customerName = getCustomerName(cxId)?.toLowerCase() ?? "";
    return cxId.toLowerCase().includes(query) || customerName.includes(query);
  }

  function isPendingRemoval(cxId: string): boolean {
    return changes.some(
      c => c.flagName === flagName && c.cxId === cxId && c.type === CHANGE_TYPE_REMOVE
    );
  }

  // Get pending additions for this flag
  const pendingAdditions = changes.filter(
    c => c.flagName === flagName && c.type === CHANGE_TYPE_ADD
  );

  const filteredValues = values.filter(matchesSearch);
  const hasCustomerValues = values.length > 0 || pendingAdditions.length > 0;
  const hasFilteredValues = filteredValues.length > 0 || pendingAdditions.length > 0;
  const isGlobalFlag = flag.values === undefined;

  // Hide the section entirely if searching and no matches
  if (searchQuery.trim() && !hasFilteredValues && hasCustomerValues) {
    return null;
  }

  return (
    <Box mb={6} p={4} borderWidth="1px" borderRadius="md" borderColor="gray.200" bg="white">
      <Flex alignItems="center" mb={3}>
        <Text fontWeight="bold" fontSize="md" textDecoration="underline">
          {flagName}
        </Text>
        {!flag.enabled && (
          <Text ml={2} fontSize="sm" color="red.500">
            (globally disabled)
          </Text>
        )}
      </Flex>

      {isGlobalFlag ? (
        <Text fontSize="sm" color="gray.500">
          Global flag (no per-customer values)
        </Text>
      ) : hasFilteredValues ? (
        <Flex flexWrap="wrap" gap={2}>
          {/* Show pending additions first */}
          {pendingAdditions.map(change => (
            <CustomerChip
              key={`pending-${change.cxId}`}
              cxId={change.cxId}
              customerName={getCustomerName(change.cxId)}
              searchQuery={searchQuery}
              isPendingAddition={true}
              onClick={onStageRemoval ? () => onStageRemoval(change.cxId) : undefined}
            />
          ))}
          {/* Show existing values */}
          {filteredValues.map(cxId => (
            <CustomerChip
              key={cxId}
              cxId={cxId}
              customerName={getCustomerName(cxId)}
              searchQuery={searchQuery}
              isPendingRemoval={isPendingRemoval(cxId)}
              onClick={onStageRemoval ? () => onStageRemoval(cxId) : undefined}
            />
          ))}
        </Flex>
      ) : (
        <Text fontSize="sm" color="gray.500">
          {searchQuery.trim() ? "No customers match the search" : "No customers"}
        </Text>
      )}
    </Box>
  );
}
